import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { R } from '../ProfileResources.resx';
import { AddHouseholdMemberModal } from '@/features/profile/householdMembers/AddHouseholdMemberModal';
import { selfResponseAtom } from '@/features/profile/householdMembers/householdMembersAtoms';
import { IAdultHouseholdMemberDto, ISelfResponse } from '@/shared/interfaces';
import {
    QueryClient,
    QueryClientProvider,
    useMutation,
    useQuery,
} from '@tanstack/react-query';
import trashIcon from '../../../../wwwroot/images/icons/trash.svg';
import { useHydrateAtoms } from 'jotai/utils';
import { Button } from '@/components/Button';
import { PartnerStatusEnum } from '@/shared/enums';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import * as React from 'react';
import { useAtom } from 'jotai/index';
import { fetchProfile } from '@/api/profile/fetchProfile';
import { toast } from '@/shared/Toast';
import { deleteHouseholdMember } from '@/api/profile/deleteHouseholdMember';

export const HouseholdMembersApp = (
    containerQuery: string,
    selfResponse: ISelfResponse,
    canInvite: boolean,
    terms: string,
) => {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    const queryClient = new QueryClient();
    root.render(
        <QueryClientProvider client={queryClient}>
            <HouseholdMembers
                self={selfResponse}
                canInvite={canInvite}
                terms={terms}
            />
        </QueryClientProvider>,
    );
};

type HouseholdMembersProps = {
    self: ISelfResponse;
    canInvite: boolean;
    terms: string;
};

const HouseholdMembers = ({
    self,
    canInvite,
    terms,
}: HouseholdMembersProps) => {
    useHydrateAtoms([[selfResponseAtom, self]]);
    const [profileAtom, setProfileAtom] = useAtom(selfResponseAtom);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] =
        useState<IAdultHouseholdMemberDto | null>(null);
    const { refetch } = useQuery({
        queryKey: ['todos'],
        queryFn: fetchProfile,
        enabled: false,
    });

    const handleRefetch = async () => {
        try {
            const { data: updatedProfile } = await refetch();
            setProfileAtom(updatedProfile);
        } catch (e) {
            window.location.reload();
        }
    };

    const deleteMember = useMutation({
        mutationFn: deleteHouseholdMember,
        onSuccess: handleRefetch,
        onSettled: () => setMemberToDelete(null),
        onError: () => {
            toast.addToast({
                type: 'error',
                message: R.ErrorDeletePartner,
            });
        },
    });

    const performDelete = async () => {
        deleteMember.mutate(memberToDelete);
    };

    return (
        <>
            <div className="flex flex-wrap">
                <div className="flex h-full flex-col">
                    <p className="max-w-prose whitespace-pre-line">{terms}</p>

                    <div aria-describedby="householdMembers-profile-description">
                        {profileAtom.AdultHouseholdMembers.map((memberDto) => {
                            return (
                                <div
                                    className="flex flex-row pb-5"
                                    key={memberDto.Cpr}
                                >
                                    <div
                                        className="w-full max-w-[420px] rounded-2xl border border-grey-300"
                                        tab-index="0"
                                        aria-labelledby="member-name"
                                    >
                                        <div className="my-1 flex h-16 flex-col justify-center px-4">
                                            <div
                                                className="text-lg font-bold leading-normal text-grey-900"
                                                id="member-name"
                                            >
                                                {memberDto.Name}
                                            </div>
                                            <div className="text-sm font-normal leading-normal text-grey-700">
                                                {getPartnerStatus(
                                                    memberDto.Status,
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex w-1/5 items-center self-center px-2 text-center md:w-auto">
                                        <button
                                            onClick={() =>
                                                setMemberToDelete(memberDto)
                                            }
                                        >
                                            <img
                                                id="delete-action"
                                                width="24"
                                                height="24"
                                                src={trashIcon}
                                                alt={
                                                    R.OpenDeleteHouseholdMemberModalA11y
                                                }
                                                className="m-3"
                                            />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {canInvite && (
                    <Button onClick={() => setIsAddModalOpen(true)}>
                        {R.InvitePartner}
                    </Button>
                )}
            </div>

            {isAddModalOpen && (
                <AddHouseholdMemberModal
                    onClose={(refetch) => {
                        if (refetch) {
                            handleRefetch();
                        }
                        setIsAddModalOpen(false);
                    }}
                />
            )}

            {memberToDelete && (
                <ConfirmationModal
                    title={R.RemovePartner}
                    onClose={() => setMemberToDelete(null)}
                    message={R.RemovePartnerText}
                    confirmButtonText={R.RemoveHouseholdMember}
                    confirmButtonClick={performDelete}
                    cancelButtonText={R.KeepHouseholdMember}
                    confirmIsProcessing={deleteMember.isPending}
                />
            )}
        </>
    );
};

const getPartnerStatus = (status: PartnerStatusEnum): string => {
    switch (status) {
        case PartnerStatusEnum.NoPartner:
            return R.NoPartner;
        case PartnerStatusEnum.Invited:
            return R.Invited;
        case PartnerStatusEnum.Active:
            return R.Active;
        default:
            return '';
    }
};
